import React, { useState, useEffect, useRef } from 'react';
import { database, createRealtimeListener } from '../firebase';
import { ref, set, get } from 'firebase/database';
import { aiChat } from '../lib/aiApi';
import {
  ArrowLeft,
  Plus,
  Target,
  CheckSquare,
  Clock,
  X,
  ChevronRight,
  TrendingUp,
  Zap,
  Flag,
  Calendar,
  AlertTriangle,
  Trophy,
  Timer,
  BarChart3,
  Milestone,
  StickyNote,
  Play,
  Pause,
  RotateCcw,
  Check,
  BookOpen,
  Rocket,
  ListChecks,
  RefreshCw,
  Sparkles,
  User
} from 'lucide-react';
import './ObjectiveDetailPage.css';

const ObjectiveDetailPage = ({
  project,
  userId,
  objectiveIndex,
  objective,
  onBack,
  onUpdate
}) => {
  const [objectiveData, setObjectiveData] = useState(objective);
  const [subItems, setSubItems] = useState(objective?.subItems || []);
  const [addingSubItem, setAddingSubItem] = useState(false);
  const [newSubItemText, setNewSubItemText] = useState('');
  const [notes, setNotes] = useState(objective?.notes || '');
  const [deadline, setDeadline] = useState(objective?.deadline || '');
  const [priority, setPriority] = useState(objective?.priority || 'medium');
  const [showNotes, setShowNotes] = useState(false);
  const [productContext, setProductContext] = useState(objective?.productContext || '');
  const [_showProductContext, _setShowProductContext] = useState(true); // eslint-disable-line no-unused-vars
  const [milestones, setMilestones] = useState(objective?.milestones || []);
  const [addingMilestone, setAddingMilestone] = useState(false);
  const [newMilestoneText, setNewMilestoneText] = useState('');
  const [focusTimer, setFocusTimer] = useState(0);
  const [timerRunning, setTimerRunning] = useState(false);
  const [totalTimeSpent, setTotalTimeSpent] = useState(objective?.totalTimeSpent || 0);
  const timerRef = useRef(null);

  // AI Advice State
  const [aiAdvice, setAiAdvice] = useState(objective?.cachedAdvice || null);
  const [adviceLoading, setAdviceLoading] = useState(false);
  const [adviceError, setAdviceError] = useState(null);
  const [_projectContext, setProjectContext] = useState(null); // eslint-disable-line no-unused-vars

  // Project Documents (AI-generated)
  const [projectDocs, setProjectDocs] = useState([]);
  const [savingDocs, setSavingDocs] = useState(false);
  const [expandedDoc, setExpandedDoc] = useState(null);

  // Content Tabs
  const [activeTab, setActiveTab] = useState('analysis');

  // Interactive Analysis State
  const [expandedItems, setExpandedItems] = useState({});
  const [expandedGaps, setExpandedGaps] = useState({});
  const [expandedInsights, setExpandedInsights] = useState({});
  const [expandedFeatures, setExpandedFeatures] = useState({});

  // Toggle expanded state for interactive items
  const toggleExpanded = (setter, key) => {
    setter(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // Add a gap as a sub-objective task
  const addGapAsTask = async (gap) => {
    const taskText = typeof gap === 'object' ? gap.suggestedTask || gap.gap : gap;
    const newSubItems = [...subItems, { text: taskText, completed: false }];
    try {
      await set(ref(database, `users/${userId}/projects/${project.id}/goals/${objectiveIndex}/subItems`), newSubItems);
      setSubItems(newSubItems);
      onUpdate?.();
    } catch (err) {
      console.error('Error adding gap as task:', err);
    }
  };

  // Load deep project context for AI
  const loadProjectContext = async () => {
    if (!userId || !project?.id) return null;

    try {
      // Load project data bank (ECB - Entity Context Bank)
      const dataBankRef = ref(database, `users/${userId}/projects/${project.id}/dataBank`);
      const dataBankSnap = await get(dataBankRef);
      const dataBank = dataBankSnap.val() || [];

      // Load all project goals for full scope
      const goalsRef = ref(database, `users/${userId}/projects/${project.id}/goals`);
      const goalsSnap = await get(goalsRef);
      const allGoals = goalsSnap.val() || [];

      // Load all project tasks
      const todosRef = ref(database, `users/${userId}/projects/${project.id}/todos`);
      const todosSnap = await get(todosRef);
      const allTodos = todosSnap.val() || [];

      // Load project end goal/vision if set
      const endGoalRef = ref(database, `users/${userId}/projects/${project.id}/endGoal`);
      const endGoalSnap = await get(endGoalRef);
      const endGoal = endGoalSnap.val() || null;

      // Load project files metadata
      const filesRef = ref(database, `users/${userId}/projects/${project.id}/files`);
      const filesSnap = await get(filesRef);
      const projectFiles = filesSnap.val() || [];

      // Load project updates/notes
      const updatesRef = ref(database, `users/${userId}/projects/${project.id}/updates`);
      const updatesSnap = await get(updatesRef);
      const projectUpdates = updatesSnap.val() || [];

      // Load connected repository data
      const repoDataRef = ref(database, `users/${userId}/projects/${project.id}/repoData`);
      const repoDataSnap = await get(repoDataRef);
      const repoData = repoDataSnap.val() || null;

      // Load AI-generated project documents
      const docsRef = ref(database, `users/${userId}/projects/${project.id}/projectDocs`);
      const docsSnap = await get(docsRef);
      const existingDocs = docsSnap.val() || [];
      setProjectDocs(Array.isArray(existingDocs) ? existingDocs : []);

      // Load user profile for personal context
      const profileRef = ref(database, `users/${userId}/profile`);
      const profileSnap = await get(profileRef);
      const userProfile = profileSnap.val() || {};

      // Load Personal Memory Bank (PMB) for preferences
      const pmbRef = ref(database, `users/${userId}/personalProfileBank`);
      const pmbSnap = await get(pmbRef);
      const pmb = pmbSnap.val() || [];

      // Extract key context from data bank
      const answeredQuestions = dataBank.filter(e => e.type === 'AI_QUESTION_ANSWERED');
      const objectiveContexts = dataBank.filter(e => e.type === 'OBJECTIVE_CONTEXT');
      const completedItems = dataBank.filter(e =>
        e.type?.includes('COMPLETED') && !e.type?.includes('UNCOMPLETED')
      ).slice(-10);

      // Extract industry/domain hints from project name and goals
      const allText = [
        project?.name || '',
        endGoal || '',
        ...allGoals.map(g => g?.title || ''),
        ...allTodos.map(t => t?.task || ''),
        ...answeredQuestions.map(q => `${q.data?.question || ''} ${q.data?.answer || ''}`)
      ].join(' ').toLowerCase();

      // Detect industry/domain from keywords
      const industryKeywords = {
        tech: ['app', 'software', 'code', 'api', 'platform', 'saas', 'tech', 'developer', 'engineering'],
        ecommerce: ['store', 'shop', 'product', 'sell', 'commerce', 'retail', 'inventory', 'customer'],
        finance: ['finance', 'money', 'invest', 'trading', 'bank', 'crypto', 'budget', 'revenue'],
        health: ['health', 'fitness', 'medical', 'wellness', 'nutrition', 'therapy', 'patient'],
        education: ['learn', 'course', 'teach', 'student', 'training', 'education', 'skill'],
        creative: ['design', 'art', 'content', 'media', 'brand', 'creative', 'video', 'music'],
        consulting: ['client', 'consulting', 'agency', 'service', 'strategy', 'business'],
        marketing: ['marketing', 'growth', 'audience', 'campaign', 'ads', 'social', 'brand'],
        realestate: ['property', 'real estate', 'housing', 'rental', 'lease', 'building'],
        food: ['food', 'restaurant', 'recipe', 'menu', 'chef', 'catering', 'cuisine']
      };

      const detectedIndustries = [];
      for (const [industry, keywords] of Object.entries(industryKeywords)) {
        if (keywords.some(kw => allText.includes(kw))) {
          detectedIndustries.push(industry);
        }
      }

      const context = {
        // Project specifics
        projectName: project?.name,
        projectVision: endGoal,
        allObjectives: allGoals.map(g => ({
          title: g?.title,
          completed: g?.completed,
          subCount: g?.subItems?.length || 0
        })),
        allTasks: allTodos.map(t => ({
          task: t?.task,
          completed: t?.completed
        })),

        // Project files (documentation)
        projectFiles: projectFiles.map(f => ({
          name: f?.name,
          type: f?.type
        })),

        // Project updates/log
        projectUpdates: projectUpdates.slice(-10).map(u => u?.text || u),

        // Product context from this objective
        productContext: productContext,

        // User context
        userName: userProfile?.name,
        userVision: userProfile?.vision,
        userObstacles: userProfile?.obstacles,

        // Knowledge from conversations
        answeredQuestions: answeredQuestions.slice(-5).map(q => ({
          question: q.data?.question,
          answer: q.data?.answer
        })),

        // Objective-specific context
        objectiveDeadlines: objectiveContexts.filter(c => c.data?.contextType === 'deadline'),
        objectiveStakeholders: objectiveContexts.filter(c => c.data?.contextType === 'stakeholder'),
        objectiveSuccessCriteria: objectiveContexts.filter(c => c.data?.contextType === 'success_criteria'),
        objectiveConstraints: objectiveContexts.filter(c => c.data?.contextType === 'constraint'),
        objectiveRisks: objectiveContexts.filter(c => c.data?.contextType === 'risk'),

        // Personal preferences from PMB
        userPreferences: pmb.slice(-5),

        // Recent activity
        recentCompletions: completedItems,

        // Detected industry/domain
        detectedIndustries,

        // Connected repository data (fileContents is now an array of {name, content})
        repository: repoData ? {
          url: repoData.url,
          owner: repoData.owner,
          repo: repoData.repo,
          description: repoData.description,
          language: repoData.language,
          techStack: repoData.techStack,
          totalFiles: repoData.totalFiles,
          fileStructure: repoData.fileStructure?.slice(0, 50), // Top 50 files
          readme: repoData.fileContents?.find?.(f => f.name === 'README.md')?.content,
          packageJson: repoData.fileContents?.find?.(f => f.name === 'package.json')?.content,
          lastUpdated: repoData.lastUpdated
        } : null,

        // AI-generated project documents
        existingDocs: existingDocs.map(d => ({
          title: d.title,
          type: d.type,
          content: d.content,
          createdAt: d.createdAt
        })),

        // Stats
        totalObjectives: allGoals.length,
        completedObjectives: allGoals.filter(g => g?.completed).length,
        totalTasks: allTodos.length,
        completedTasks: allTodos.filter(t => t?.completed).length
      };

      setProjectContext(context);
      return context;
    } catch (e) {
      console.error('Failed to load project context:', e);
      return null;
    }
  };

  // Generate AI advice from professional intelligence frameworks
  const generateAIAdvice = async (forceRefresh = false) => {
    // Use cached advice if available and not forcing refresh
    if (aiAdvice && !forceRefresh) return;

    setAdviceLoading(true);
    setAdviceError(null);

    // Load deep project context first
    const context = await loadProjectContext();

    const systemPrompt = `You are a senior startup CTO/product advisor who analyzes projects and gives SPECIFIC build guidance.

FIRST: Analyze all provided context (data bank, files, objectives, updates, repository code, existing documents) to understand:
- What type of product this is (app, platform, service, etc.)
- Current development stage
- What exists vs what's missing

THEN: Generate a PROJECT ANALYSIS, SPECIFIC ACTION PLAN, and CREATE NECESSARY DOCUMENTS.

YOUR EXPERTISE:
- Product architecture and technical decisions
- Startup methodology (Lean, Agile, OKRs)
- Go-to-market strategies with real benchmarks
- Competitive analysis and positioning
- Resource allocation and prioritization
- Technical documentation and user flow mapping

CRITICAL RULES:
1. ANALYZE what exists in the data bank and existing documents - don't ask for info that's already there
2. IDENTIFY GAPS - what's missing for this stage? (designs, user flows, API specs, etc.)
3. GIVE SPECIFIC DELIVERABLES - not "create designs" but "Create these 5 screens: Login, Dashboard, [Feature], Settings, Onboarding"
4. PROVIDE ACTUAL SPECIFICATIONS - not "build an API" but "Build REST endpoints: POST /users, GET /products, etc."
5. INCLUDE REAL NUMBERS - timelines, costs, benchmarks, metrics
6. CREATE DOCUMENTS - When information is missing, CREATE the document yourself with reasonable assumptions based on the codebase

DOCUMENT GENERATION:
- If user flows are missing, CREATE a user flow document
- If tech specs are missing, CREATE them by analyzing the repository
- If business model is unclear, CREATE a business model canvas
- Don't just say "needs user flow" - WRITE the user flow

Respond in this EXACT JSON format:
{
  "projectAnalysis": {
    "productType": "What this product is (e.g., 'B2B SaaS procurement platform', 'Mobile fitness app')",
    "currentStage": "Where they are (e.g., 'MVP built, in pilot', 'Concept phase', 'Post-launch scaling')",
    "stageProgress": 65,
    "whatExists": [
      {
        "item": "Name of what exists",
        "status": "complete|partial|needs-review",
        "details": "Specific details about this item - what it includes, quality assessment",
        "evidence": "Where this was found or how you determined it exists"
      }
    ],
    "criticalGaps": [
      {
        "gap": "Specific missing piece",
        "priority": "critical|high|medium",
        "effort": "1-2 days|1 week|2-4 weeks",
        "impact": "What happens if this isn't addressed",
        "suggestedTask": "Actionable task description to address this gap"
      }
    ],
    "keyInsights": [
      {
        "insight": "A key observation about the project",
        "type": "strength|weakness|opportunity|risk",
        "recommendation": "What to do about it"
      }
    ]
  },
  "projectInfo": {
    "coreProblem": "The specific problem being solved in one sentence",
    "problemDetails": "Deeper explanation of the pain point and why it matters",
    "solution": "How the product solves it",
    "solutionDifferentiator": "What makes this solution unique vs alternatives",
    "targetUser": {
      "persona": "Primary user persona name",
      "role": "Their job/role",
      "painPoints": ["Pain point 1", "Pain point 2"],
      "goals": ["What they want to achieve"]
    },
    "keyFeatures": [
      {
        "name": "Feature name",
        "description": "What it does",
        "status": "built|in-progress|planned",
        "userBenefit": "Why users care about this"
      }
    ],
    "techStack": {
      "frontend": "Frontend technologies",
      "backend": "Backend technologies",
      "database": "Database",
      "infrastructure": "Hosting/deployment"
    },
    "competitors": [
      {
        "name": "Competitor name",
        "weakness": "Their main weakness",
        "ourAdvantage": "How we beat them"
      }
    ],
    "marketSize": "TAM/SAM/SOM estimate if determinable"
  },
  "generatedDocuments": [
    {
      "title": "Document title (e.g., 'User Flow: Onboarding', 'Technical Architecture', 'API Specification')",
      "type": "user_flow|tech_spec|business_doc|feature_spec|api_spec",
      "content": "FULL document content - be detailed and thorough. Include diagrams as ASCII art or Mermaid syntax if helpful. Write at least 200 words per document."
    }
  ],
  "immediateActions": [
    {
      "action": "Specific deliverable to create NOW",
      "details": "Exact specifications (screen names, API endpoints, document sections)",
      "timeEstimate": "How long this takes",
      "why": "Why this is the priority"
    },
    {
      "action": "Second priority action",
      "details": "Exact specifications",
      "timeEstimate": "Time estimate",
      "why": "Why this matters"
    },
    {
      "action": "Third priority action",
      "details": "Exact specifications",
      "timeEstimate": "Time estimate",
      "why": "Why this matters"
    }
  ],
  "requiredDeliverables": {
    "designs": {
      "status": "exists/missing/partial",
      "needed": ["Screen 1: description", "Screen 2: description"]
    },
    "userFlows": {
      "status": "exists/missing/partial",
      "needed": ["Flow 1: step by step", "Flow 2: step by step"]
    },
    "technicalSpecs": {
      "status": "exists/missing/partial",
      "needed": ["API endpoints list", "Database schema", "etc."]
    },
    "businessDocs": {
      "status": "exists/missing/partial",
      "needed": ["Pricing model", "Go-to-market plan", "etc."]
    }
  },
  "startupAdvice": {
    "stage": "What startup stage they're in",
    "focus": "The ONE thing to focus on this month",
    "avoid": "What NOT to waste time on right now",
    "milestone": "Next major milestone to hit",
    "metrics": ["Key metric 1 to track", "Key metric 2"]
  },
  "focusingQuestion": "The ONE question that determines success for this objective"
}`;

    // Build comprehensive user prompt with all context
    let userPrompt = `You are helping BUILD this product. Give SPECIFIC product advice, not general business advice.

═══════════════════════════════════════════════════════════════
CURRENT OBJECTIVE
═══════════════════════════════════════════════════════════════
TITLE: "${objective?.title}"
PRIORITY: ${priority}
${deadline ? `DEADLINE: ${deadline}` : 'NO DEADLINE SET'}
${notes ? `USER NOTES: "${notes}"` : ''}
SUB-OBJECTIVES DEFINED: ${subItems.length} (${subItems.filter(s => s?.completed).length} completed)
${milestones.length > 0 ? `MILESTONES SET: ${milestones.length} (${milestones.filter(m => m?.completed).length} achieved)` : ''}

${productContext ? `
═══════════════════════════════════════════════════════════════
PRODUCT INTEL (USER PROVIDED)
═══════════════════════════════════════════════════════════════
${productContext}
` : ''}
`;

    if (context) {
      userPrompt += `
═══════════════════════════════════════════════════════════════
PROJECT CONTEXT
═══════════════════════════════════════════════════════════════
PROJECT NAME: "${context.projectName}"
${context.projectVision ? `PROJECT VISION/END GOAL: "${context.projectVision}"` : ''}
${context.detectedIndustries.length > 0 ? `DETECTED INDUSTRIES: ${context.detectedIndustries.join(', ')}` : ''}

ALL PROJECT OBJECTIVES (${context.totalObjectives} total, ${context.completedObjectives} completed):
${context.allObjectives.map((o, i) => `  ${i + 1}. ${o.completed ? '✓' : '○'} ${o.title} (${o.subCount} sub-items)`).join('\n')}

${context.allTasks.length > 0 ? `
KEY TASKS IN PROJECT:
${context.allTasks.slice(0, 8).map(t => `  ${t.completed ? '✓' : '○'} ${t.task}`).join('\n')}
` : ''}

${context.projectFiles && context.projectFiles.length > 0 ? `
ATTACHED DOCUMENTATION (${context.projectFiles.length} files):
${context.projectFiles.map(f => `  - ${f.name} (${f.type || 'file'})`).join('\n')}
NOTE: I can see file names but not contents. Key info should be in PRODUCT INTEL section.
` : ''}

${context.projectUpdates && context.projectUpdates.length > 0 ? `
RECENT PROJECT UPDATES:
${context.projectUpdates.slice(-5).map(u => `  • ${u}`).join('\n')}
` : ''}
`;

      // Add repository context if connected
      if (context.repository) {
        userPrompt += `
═══════════════════════════════════════════════════════════════
CONNECTED REPOSITORY (GitHub)
═══════════════════════════════════════════════════════════════
REPO: ${context.repository.owner}/${context.repository.repo}
URL: ${context.repository.url}
${context.repository.description ? `DESCRIPTION: ${context.repository.description}` : ''}
MAIN LANGUAGE: ${context.repository.language || 'Unknown'}
TECH STACK: ${context.repository.techStack?.join(', ') || 'Unknown'}
TOTAL FILES: ${context.repository.totalFiles}

${context.repository.fileStructure?.length > 0 ? `
KEY FILES/STRUCTURE:
${context.repository.fileStructure.filter(f => f.type === 'blob').slice(0, 30).map(f => `  - ${f.path}`).join('\n')}
` : ''}

${context.repository.readme ? `
README CONTENT:
${context.repository.readme.slice(0, 2000)}
${context.repository.readme.length > 2000 ? '... (truncated)' : ''}
` : ''}

${context.repository.packageJson ? `
PACKAGE.JSON (dependencies):
${context.repository.packageJson.slice(0, 1500)}
` : ''}
`;
      }

      if (context.userName || context.userVision || context.userObstacles) {
        userPrompt += `
═══════════════════════════════════════════════════════════════
USER CONTEXT
═══════════════════════════════════════════════════════════════
${context.userName ? `NAME: ${context.userName}` : ''}
${context.userVision ? `PERSONAL VISION: "${context.userVision}"` : ''}
${context.userObstacles ? `KNOWN OBSTACLES: "${context.userObstacles}"` : ''}
`;
      }

      if (context.answeredQuestions.length > 0) {
        userPrompt += `
═══════════════════════════════════════════════════════════════
WHAT THE USER HAS SHARED (from previous conversations)
═══════════════════════════════════════════════════════════════
${context.answeredQuestions.map(q => `Q: ${q.question}\nA: "${q.answer}"`).join('\n\n')}
`;
      }

      if (context.objectiveDeadlines.length > 0 || context.objectiveStakeholders.length > 0 ||
          context.objectiveRisks.length > 0 || context.objectiveConstraints.length > 0) {
        userPrompt += `
═══════════════════════════════════════════════════════════════
ADDITIONAL CONTEXT FROM DATA BANK
═══════════════════════════════════════════════════════════════
${context.objectiveDeadlines.map(d => `DEADLINE: ${d.data?.objective} - ${d.data?.contextValue}`).join('\n')}
${context.objectiveStakeholders.map(s => `STAKEHOLDER: ${s.data?.contextValue}`).join('\n')}
${context.objectiveRisks.map(r => `RISK: ${r.data?.contextValue}`).join('\n')}
${context.objectiveConstraints.map(c => `CONSTRAINT: ${c.data?.contextValue}`).join('\n')}
`;
      }

      if (context.recentCompletions.length > 0) {
        userPrompt += `
RECENT MOMENTUM: User has completed ${context.recentCompletions.length} items recently.
`;
      }

      // Add existing AI-generated documents
      if (context.existingDocs && context.existingDocs.length > 0) {
        userPrompt += `
═══════════════════════════════════════════════════════════════
EXISTING PROJECT DOCUMENTS (previously generated)
═══════════════════════════════════════════════════════════════
${context.existingDocs.map(doc => `
📄 ${doc.title} [${doc.type}]
${doc.content}
---`).join('\n')}

NOTE: Review these existing documents. UPDATE them if needed, or CREATE NEW ones for missing areas.
`;
      }
    }

    userPrompt += `
═══════════════════════════════════════════════════════════════
YOUR TASK
═══════════════════════════════════════════════════════════════
You are the user's EXPERT PARTNER helping build this product. Provide:

1. SPECIFIC PRODUCT DECISIONS - Not "define your value prop" but "Your value prop should be X because Y"
2. CONCRETE FEATURES - Not "add features users want" but "Build these 3 specific features: A, B, C"
3. ACTUAL NUMBERS - Pricing, timelines, metrics, benchmarks
4. REAL TOOLS & TECH - Specific tech stack, tools, integrations to use
5. COMPETITIVE INTELLIGENCE - What competitors do, how to beat them

${!productContext ? `
⚠️ CRITICAL: No PRODUCT INTEL provided. You MUST include clarifyingQuestions asking:
- What does the product do specifically?
- Who is the target user and what's their main pain point?
- What's the core technology/approach?
- What competitors exist?
` : ''}

DO NOT:
- Tell them to "research" or "brainstorm" - YOU do that
- Give meta-advice like "create a strategy" - give THE strategy
- Be vague - every suggestion must be immediately actionable

YOU are the expert. Give answers, not homework.`;

    try {
      const data = await aiChat({
        task: 'mentor-chat',
        messages: [
          { role: 'system', content: systemPrompt },
          { role: 'user', content: userPrompt }
        ],
        temperature: 0.7,
        max_tokens: 3000  // Increased for document generation
      });

      const content = data.choices?.[0]?.message?.content?.trim();

      try {
        // Parse JSON response
        const jsonMatch = content.match(/\{[\s\S]*\}/);
        if (jsonMatch) {
          const advice = JSON.parse(jsonMatch[0]);
          setAiAdvice(advice);
          // Cache the advice in Firebase
          await saveObjectiveField('cachedAdvice', advice);
          await saveObjectiveField('adviceCachedAt', Date.now());

          // Save generated documents to project
          if (advice.generatedDocuments && advice.generatedDocuments.length > 0) {
            await saveGeneratedDocs(advice.generatedDocuments);
          }
        } else {
          throw new Error('No JSON found in response');
        }
      } catch (parseError) {
        console.error('Failed to parse AI advice:', parseError);
        setAdviceError('Failed to parse advice. Try regenerating.');
      }
    } catch (err) {
      console.error('AI advice error:', err);
      setAdviceError('Connection error. Please try again.');
    } finally {
      setAdviceLoading(false);
    }
  };

  // Load advice on mount
  useEffect(() => {
    if (objective?.cachedAdvice) {
      setAiAdvice(objective.cachedAdvice);
    } else {
      generateAIAdvice();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Listen for project changes
  useEffect(() => {
    if (!userId || !project?.id) return;

    const off = createRealtimeListener(`users/${userId}/projects/${project.id}/goals`, (snapshot) => {
      const goals = snapshot.val();
      if (goals && goals[objectiveIndex]) {
        const updatedObjective = goals[objectiveIndex];
        setObjectiveData(updatedObjective);
        setSubItems(updatedObjective.subItems || []);
        setNotes(updatedObjective.notes || '');
        setDeadline(updatedObjective.deadline || '');
        setPriority(updatedObjective.priority || 'medium');
        setMilestones(updatedObjective.milestones || []);
        setTotalTimeSpent(updatedObjective.totalTimeSpent || 0);
        setProductContext(updatedObjective.productContext || '');
      }
    });

    return () => off && off();
  }, [userId, project?.id, objectiveIndex]);

  // Focus timer
  useEffect(() => {
    if (timerRunning) {
      timerRef.current = setInterval(() => {
        setFocusTimer(prev => prev + 1);
      }, 1000);
    } else {
      clearInterval(timerRef.current);
    }
    return () => clearInterval(timerRef.current);
  }, [timerRunning]);

  // Save time when stopping timer
  const stopAndSaveTimer = async () => {
    setTimerRunning(false);
    const newTotal = totalTimeSpent + focusTimer;
    setTotalTimeSpent(newTotal);
    setFocusTimer(0);
    await saveObjectiveField('totalTimeSpent', newTotal);
  };

  // Format timer display
  const formatTimer = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    if (hrs > 0) {
      return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Save a single field to the objective
  const saveObjectiveField = async (field, value) => {
    if (!userId || !project?.id) return;
    try {
      const goalsRef = ref(database, `users/${userId}/projects/${project.id}/goals`);
      const snapshot = await get(goalsRef);
      const goals = snapshot.val() || [];
      goals[objectiveIndex] = { ...goals[objectiveIndex], [field]: value };
      await set(goalsRef, goals);
      onUpdate?.();
    } catch (e) {
      console.error('Failed to save objective field:', e);
    }
  };

  // Save generated documents to project
  const saveGeneratedDocs = async (newDocs) => {
    if (!userId || !project?.id || !newDocs?.length) return;

    setSavingDocs(true);
    try {
      const docsRef = ref(database, `users/${userId}/projects/${project.id}/projectDocs`);
      const snapshot = await get(docsRef);
      const existingDocs = snapshot.val() || [];

      // Add timestamp and merge with existing (avoid duplicates by title)
      const existingTitles = new Set(existingDocs.map(d => d.title));
      const docsToAdd = newDocs
        .filter(doc => !existingTitles.has(doc.title))
        .map(doc => ({
          ...doc,
          createdAt: Date.now(),
          objectiveTitle: objective?.title || 'Unknown'
        }));

      const updatedDocs = [...existingDocs, ...docsToAdd];
      await set(docsRef, updatedDocs);
      setProjectDocs(updatedDocs);

      console.log(`Saved ${docsToAdd.length} new documents`);
    } catch (e) {
      console.error('Failed to save generated docs:', e);
    } finally {
      setSavingDocs(false);
    }
  };

  // Delete a project document
  const deleteDoc = async (docIndex) => {
    if (!userId || !project?.id) return;
    try {
      const updatedDocs = projectDocs.filter((_, i) => i !== docIndex);
      const docsRef = ref(database, `users/${userId}/projects/${project.id}/projectDocs`);
      await set(docsRef, updatedDocs);
      setProjectDocs(updatedDocs);
    } catch (e) {
      console.error('Failed to delete doc:', e);
    }
  };

  // Add sub-item
  const addSubItem = async () => {
    if (!newSubItemText.trim() || !userId) return;
    try {
      const newSubItems = [...subItems, { text: newSubItemText.trim(), completed: false, createdAt: Date.now() }];
      await saveObjectiveField('subItems', newSubItems);
      setNewSubItemText('');
      setAddingSubItem(false);
    } catch (e) {
      console.error('Failed to add sub-item:', e);
    }
  };

  // Toggle sub-item
  const toggleSubItem = async (index) => {
    try {
      const newSubItems = [...subItems];
      const item = newSubItems[index];
      newSubItems[index] = {
        ...item,
        text: typeof item === 'object' ? item.text : item,
        completed: !(typeof item === 'object' ? item.completed : false),
        completedAt: !(typeof item === 'object' ? item.completed : false) ? Date.now() : null
      };
      await saveObjectiveField('subItems', newSubItems);
    } catch (e) {
      console.error('Failed to toggle sub-item:', e);
    }
  };

  // Delete sub-item
  const deleteSubItem = async (index) => {
    try {
      const newSubItems = subItems.filter((_, i) => i !== index);
      await saveObjectiveField('subItems', newSubItems);
    } catch (e) {
      console.error('Failed to delete sub-item:', e);
    }
  };

  // Add milestone
  const addMilestone = async () => {
    if (!newMilestoneText.trim() || !userId) return;
    try {
      const newMilestones = [...milestones, {
        text: newMilestoneText.trim(),
        completed: false,
        createdAt: Date.now()
      }];
      await saveObjectiveField('milestones', newMilestones);
      setNewMilestoneText('');
      setAddingMilestone(false);
    } catch (e) {
      console.error('Failed to add milestone:', e);
    }
  };

  // Toggle milestone
  const toggleMilestone = async (index) => {
    try {
      const newMilestones = [...milestones];
      newMilestones[index] = {
        ...newMilestones[index],
        completed: !newMilestones[index].completed,
        completedAt: !newMilestones[index].completed ? Date.now() : null
      };
      await saveObjectiveField('milestones', newMilestones);
    } catch (e) {
      console.error('Failed to toggle milestone:', e);
    }
  };

  // Save notes with debounce
  const saveNotes = async (value) => {
    setNotes(value);
    await saveObjectiveField('notes', value);
  };

  // Save deadline
  const saveDeadline = async (value) => {
    setDeadline(value);
    await saveObjectiveField('deadline', value);
  };

  // Save priority
  const savePriority = async (value) => {
    setPriority(value);
    await saveObjectiveField('priority', value);
  };

  // Toggle objective completion
  const toggleObjectiveComplete = async () => {
    try {
      const goalsRef = ref(database, `users/${userId}/projects/${project.id}/goals`);
      const snapshot = await get(goalsRef);
      const goals = snapshot.val() || [];
      const wasCompleted = goals[objectiveIndex].completed;
      goals[objectiveIndex] = {
        ...goals[objectiveIndex],
        completed: !wasCompleted,
        completedAt: !wasCompleted ? Date.now() : null
      };
      await set(goalsRef, goals);
      onUpdate?.();
    } catch (e) {
      console.error('Failed to toggle objective:', e);
    }
  };

  // Calculate progress
  const completedSubItems = subItems.filter(s => typeof s === 'object' ? s.completed : false).length;
  const totalSubItems = subItems.length;
  const subItemProgress = totalSubItems > 0 ? Math.round((completedSubItems / totalSubItems) * 100) : 0;

  const completedMilestones = milestones.filter(m => m.completed).length;
  const totalMilestones = milestones.length;
  const milestoneProgress = totalMilestones > 0 ? Math.round((completedMilestones / totalMilestones) * 100) : 0;

  // Calculate days until deadline
  const getDaysUntilDeadline = () => {
    if (!deadline) return null;
    const deadlineDate = new Date(deadline);
    const today = new Date();
    const diffTime = deadlineDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const daysUntilDeadline = getDaysUntilDeadline();

  return (
    <div className="objective-detail-page sidebar-layout">
      {/* Header */}
      <header className="objective-header">
        <button className="back-btn" onClick={onBack}>
          <ArrowLeft size={18} />
          <span>[ BACK ]</span>
        </button>
        <div className="objective-title-section">
          <div className="objective-icon">
            <Target size={24} />
          </div>
          <div className="objective-title-content">
            <span className="objective-label">OBJECTIVE {objectiveIndex + 1}</span>
            <h1 className="objective-title">{objectiveData?.title}</h1>
          </div>
        </div>
        <div className="header-actions">
          <button
            className={`complete-objective-btn ${objectiveData?.completed ? 'completed' : ''}`}
            onClick={toggleObjectiveComplete}
          >
            {objectiveData?.completed ? (
              <>
                <Trophy size={16} />
                <span>COMPLETED</span>
              </>
            ) : (
              <>
                <Check size={16} />
                <span>MARK COMPLETE</span>
              </>
            )}
          </button>
        </div>
      </header>

      {/* Sidebar + Content Layout */}
      <div className="objective-body">
        {/* Left Sidebar - Tools & Stats */}
        <aside className="objective-sidebar">
          {/* Priority & Deadline */}
          <section className="sidebar-section meta-section">
            <div className="meta-row">
              <div className="meta-item">
                <label className="meta-label">
                  <Zap size={14} />
                  <span>PRIORITY</span>
                </label>
                <select
                  className={`priority-select priority-${priority}`}
                  value={priority}
                  onChange={(e) => savePriority(e.target.value)}
                >
                  <option value="low">LOW</option>
                  <option value="medium">MEDIUM</option>
                  <option value="high">HIGH</option>
                  <option value="critical">CRITICAL</option>
                </select>
              </div>
              <div className="meta-item">
                <label className="meta-label">
                  <Calendar size={14} />
                  <span>DEADLINE</span>
                </label>
                <input
                  type="date"
                  className="deadline-input"
                  value={deadline}
                  onChange={(e) => saveDeadline(e.target.value)}
                />
              </div>
            </div>
            {deadline && (
              <div className={`deadline-countdown ${daysUntilDeadline < 0 ? 'overdue' : daysUntilDeadline <= 3 ? 'urgent' : ''}`}>
                {daysUntilDeadline < 0 ? (
                  <>
                    <AlertTriangle size={14} />
                    <span>OVERDUE by {Math.abs(daysUntilDeadline)} days</span>
                  </>
                ) : daysUntilDeadline === 0 ? (
                  <>
                    <AlertTriangle size={14} />
                    <span>DUE TODAY</span>
                  </>
                ) : (
                  <>
                    <Clock size={14} />
                    <span>{daysUntilDeadline} days remaining</span>
                  </>
                )}
              </div>
            )}
          </section>

          {/* Focus Timer */}
          <section className="sidebar-section timer-section">
            <div className="section-header">
              <h2 className="section-title">
                <Timer size={16} />
                <span>FOCUS TIMER</span>
              </h2>
            </div>
            <div className="timer-display">
              <span className="timer-value">{formatTimer(focusTimer)}</span>
            </div>
            <div className="timer-controls">
              {!timerRunning ? (
                <button className="timer-btn start-btn" onClick={() => setTimerRunning(true)}>
                  <Play size={16} />
                  <span>START</span>
                </button>
              ) : (
                <button className="timer-btn pause-btn" onClick={() => setTimerRunning(false)}>
                  <Pause size={16} />
                  <span>PAUSE</span>
                </button>
              )}
              <button className="timer-btn stop-btn" onClick={stopAndSaveTimer} disabled={focusTimer === 0}>
                <Check size={16} />
                <span>SAVE</span>
              </button>
              <button className="timer-btn reset-btn" onClick={() => setFocusTimer(0)} disabled={timerRunning}>
                <RotateCcw size={16} />
              </button>
            </div>
            <div className="total-time">
              <span className="total-label">TOTAL TIME:</span>
              <span className="total-value">{formatTimer(totalTimeSpent)}</span>
            </div>
          </section>

          {/* Stats Overview */}
          <section className="sidebar-section stats-section">
            <div className="section-header">
              <h2 className="section-title">
                <BarChart3 size={16} />
                <span>STATISTICS</span>
              </h2>
            </div>
            <div className="stats-grid">
              <div className="stat-card">
                <span className="stat-value">{subItemProgress}%</span>
                <span className="stat-label">SUB-OBJ</span>
              </div>
              <div className="stat-card">
                <span className="stat-value">{milestoneProgress}%</span>
                <span className="stat-label">MILESTONES</span>
              </div>
              <div className="stat-card">
                <span className="stat-value">{totalSubItems}</span>
                <span className="stat-label">TASKS</span>
              </div>
              <div className="stat-card">
                <span className="stat-value">{formatTimer(totalTimeSpent)}</span>
                <span className="stat-label">TIME</span>
              </div>
            </div>
          </section>

          {/* Sub-objectives Section */}
          <section className="sidebar-section sub-objectives-section">
            <div className="section-header">
              <h2 className="section-title">
                <CheckSquare size={16} />
                <span>SUB-OBJECTIVES</span>
                <span className="section-count">{completedSubItems}/{totalSubItems}</span>
              </h2>
              <button className="add-btn" onClick={() => setAddingSubItem(true)}>
                <Plus size={14} />
              </button>
            </div>

            {totalSubItems > 0 && (
              <div className="section-progress">
                <div className="progress-track">
                  <div className="progress-fill" style={{ width: `${subItemProgress}%` }}></div>
                </div>
                <span className="progress-text">{subItemProgress}%</span>
              </div>
            )}

            <ul className="sub-items-list">
              {subItems.map((item, i) => {
                const text = typeof item === 'object' ? item.text : item;
                const completed = typeof item === 'object' ? item.completed : false;
                return (
                  <li key={i} className={`sub-item ${completed ? 'completed' : ''}`}>
                    <div
                      className={`sub-checkbox ${completed ? 'checked' : ''}`}
                      onClick={() => toggleSubItem(i)}
                    >
                      {completed && <Check size={12} />}
                    </div>
                    <span className="sub-text">{text}</span>
                    <button className="delete-sub-btn" onClick={() => deleteSubItem(i)}>
                      <X size={12} />
                    </button>
                  </li>
                );
              })}
              {subItems.length === 0 && !addingSubItem && (
                <li className="empty-message">No sub-objectives yet</li>
              )}
            </ul>

            {addingSubItem && (
              <div className="add-item-row">
                <input
                  type="text"
                  className="add-item-input"
                  placeholder="New sub-objective..."
                  value={newSubItemText}
                  onChange={(e) => setNewSubItemText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') addSubItem();
                    if (e.key === 'Escape') { setAddingSubItem(false); setNewSubItemText(''); }
                  }}
                  autoFocus
                />
                <button className="confirm-btn" onClick={addSubItem}>Add</button>
                <button className="cancel-btn" onClick={() => { setAddingSubItem(false); setNewSubItemText(''); }}>
                  <X size={14} />
                </button>
              </div>
            )}
          </section>

          {/* Milestones Section */}
          <section className="sidebar-section milestones-section">
            <div className="section-header">
              <h2 className="section-title">
                <Milestone size={16} />
                <span>MILESTONES</span>
                <span className="section-count">{completedMilestones}/{totalMilestones}</span>
              </h2>
              <button className="add-btn" onClick={() => setAddingMilestone(true)}>
                <Plus size={14} />
              </button>
            </div>

            {totalMilestones > 0 && (
              <div className="section-progress milestone-progress">
                <div className="progress-track">
                  <div className="progress-fill milestone-fill" style={{ width: `${milestoneProgress}%` }}></div>
                </div>
                <span className="progress-text">{milestoneProgress}%</span>
              </div>
            )}

            <ul className="milestones-list">
              {milestones.map((milestone, i) => (
                <li key={i} className={`milestone-item ${milestone.completed ? 'completed' : ''}`}>
                  <div
                    className={`milestone-marker ${milestone.completed ? 'achieved' : ''}`}
                    onClick={() => toggleMilestone(i)}
                  >
                    <Flag size={12} />
                  </div>
                  <span className="milestone-text">{milestone.text}</span>
                </li>
              ))}
              {milestones.length === 0 && !addingMilestone && (
                <li className="empty-message">No milestones set</li>
              )}
            </ul>

            {addingMilestone && (
              <div className="add-item-row">
                <input
                  type="text"
                  className="add-item-input"
                  placeholder="New milestone..."
                  value={newMilestoneText}
                  onChange={(e) => setNewMilestoneText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') addMilestone();
                    if (e.key === 'Escape') { setAddingMilestone(false); setNewMilestoneText(''); }
                  }}
                  autoFocus
                />
                <button className="confirm-btn" onClick={addMilestone}>Add</button>
                <button className="cancel-btn" onClick={() => { setAddingMilestone(false); setNewMilestoneText(''); }}>
                  <X size={14} />
                </button>
              </div>
            )}
          </section>

          {/* Notes Section */}
          <section className="sidebar-section notes-section">
            <div className="section-header" onClick={() => setShowNotes(!showNotes)}>
              <h2 className="section-title">
                <StickyNote size={16} />
                <span>NOTES</span>
              </h2>
              <ChevronRight size={16} className={`expand-icon ${showNotes ? 'expanded' : ''}`} />
            </div>
            {showNotes && (
              <textarea
                className="notes-textarea"
                placeholder="Add notes, thoughts, blockers..."
                value={notes}
                onChange={(e) => saveNotes(e.target.value)}
              />
            )}
          </section>
        </aside>

        {/* Main Content Area - Tabbed Interface */}
        <main className="objective-content">
          {/* Tab Navigation */}
          <div className="content-tabs">
            <div className="tabs-header">
              <div className="tabs-nav">
                <button
                  className={`tab-btn ${activeTab === 'analysis' ? 'active' : ''}`}
                  onClick={() => setActiveTab('analysis')}
                >
                  <Target size={14} />
                  <span>Analysis</span>
                </button>
                <button
                  className={`tab-btn ${activeTab === 'actions' ? 'active' : ''}`}
                  onClick={() => setActiveTab('actions')}
                >
                  <Rocket size={14} />
                  <span>Actions</span>
                </button>
                <button
                  className={`tab-btn ${activeTab === 'deliverables' ? 'active' : ''}`}
                  onClick={() => setActiveTab('deliverables')}
                >
                  <ListChecks size={14} />
                  <span>Deliverables</span>
                </button>
                <button
                  className={`tab-btn ${activeTab === 'guidance' ? 'active' : ''}`}
                  onClick={() => setActiveTab('guidance')}
                >
                  <TrendingUp size={14} />
                  <span>Guidance</span>
                </button>
                <button
                  className={`tab-btn ${activeTab === 'documents' ? 'active' : ''}`}
                  onClick={() => setActiveTab('documents')}
                >
                  <BookOpen size={14} />
                  <span>Documents</span>
                  {(aiAdvice?.generatedDocuments?.length > 0 || projectDocs.length > 0) && (
                    <span className="tab-badge">{(aiAdvice?.generatedDocuments?.length || 0) + projectDocs.length}</span>
                  )}
                </button>
              </div>
              <button
                className="refresh-advice-btn"
                onClick={() => generateAIAdvice(true)}
                disabled={adviceLoading}
              >
                <RefreshCw size={14} className={adviceLoading ? 'spinning' : ''} />
                <span>{adviceLoading ? 'ANALYZING...' : 'REGENERATE'}</span>
              </button>
            </div>

            {/* Loading & Error States */}
            {adviceLoading && !aiAdvice && (
              <div className="advice-loading">
                <div className="loading-spinner"></div>
                <span>Analyzing your product context...</span>
              </div>
            )}

            {adviceError && (
              <div className="advice-error">
                <AlertTriangle size={16} />
                <span>{adviceError}</span>
              </div>
            )}

            {/* Tab Content */}
            <div className="tab-content">
              {/* ANALYSIS TAB */}
              {activeTab === 'analysis' && aiAdvice && (
                <div className="tab-panel analysis-panel">
                  {/* Project Analysis Section */}
                  {aiAdvice.projectAnalysis && (
                    <div className="interactive-analysis">
                      {/* Header with Stage Progress */}
                      <div className="analysis-hero">
                        <div className="hero-left">
                          <div className="product-type-badge">
                            <Target size={14} />
                            <span>{aiAdvice.projectAnalysis.productType}</span>
                          </div>
                          <h3 className="stage-title">{aiAdvice.projectAnalysis.currentStage}</h3>
                        </div>
                        {aiAdvice.projectAnalysis.stageProgress && (
                          <div className="stage-progress-ring">
                            <svg viewBox="0 0 36 36" className="circular-chart">
                              <path className="circle-bg" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
                              <path className="circle" strokeDasharray={`${aiAdvice.projectAnalysis.stageProgress}, 100`} d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
                            </svg>
                            <span className="progress-number">{aiAdvice.projectAnalysis.stageProgress}%</span>
                          </div>
                        )}
                      </div>

                      {/* What Exists - Interactive Cards */}
                      {aiAdvice.projectAnalysis.whatExists?.length > 0 && (
                        <div className="analysis-section exists-section">
                          <div className="section-label">
                            <Check size={14} />
                            <span>WHAT EXISTS</span>
                            <span className="count-badge">{aiAdvice.projectAnalysis.whatExists.length}</span>
                          </div>
                          <div className="interactive-cards">
                            {aiAdvice.projectAnalysis.whatExists.map((item, i) => {
                              const isObject = typeof item === 'object';
                              const itemName = isObject ? item.item : item;
                              const isExpanded = expandedItems[`exists-${i}`];
                              return (
                                <div
                                  key={i}
                                  className={`interactive-card exists-card ${isObject ? `status-${item.status}` : ''} ${isExpanded ? 'expanded' : ''}`}
                                  onClick={() => isObject && toggleExpanded(setExpandedItems, `exists-${i}`)}
                                >
                                  <div className="card-main">
                                    <div className={`status-indicator ${isObject ? item.status : 'complete'}`}>
                                      <Check size={12} />
                                    </div>
                                    <span className="card-title">{itemName}</span>
                                    {isObject && (
                                      <ChevronRight size={14} className={`expand-arrow ${isExpanded ? 'rotated' : ''}`} />
                                    )}
                                  </div>
                                  {isObject && isExpanded && (
                                    <div className="card-details">
                                      {item.details && <p className="detail-text">{item.details}</p>}
                                      {item.evidence && (
                                        <div className="evidence-row">
                                          <span className="evidence-label">Evidence:</span>
                                          <span className="evidence-text">{item.evidence}</span>
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* Critical Gaps - Actionable Cards */}
                      {aiAdvice.projectAnalysis.criticalGaps?.length > 0 && (
                        <div className="analysis-section gaps-section">
                          <div className="section-label">
                            <AlertTriangle size={14} />
                            <span>CRITICAL GAPS</span>
                            <span className="count-badge urgent">{aiAdvice.projectAnalysis.criticalGaps.length}</span>
                          </div>
                          <div className="interactive-cards">
                            {aiAdvice.projectAnalysis.criticalGaps.map((gap, i) => {
                              const isObject = typeof gap === 'object';
                              const gapName = isObject ? gap.gap : gap;
                              const isExpanded = expandedGaps[`gap-${i}`];
                              return (
                                <div
                                  key={i}
                                  className={`interactive-card gap-card ${isObject ? `priority-${gap.priority}` : ''} ${isExpanded ? 'expanded' : ''}`}
                                >
                                  <div className="card-main" onClick={() => isObject && toggleExpanded(setExpandedGaps, `gap-${i}`)}>
                                    <div className={`priority-indicator ${isObject ? gap.priority : 'high'}`}>
                                      <AlertTriangle size={12} />
                                    </div>
                                    <span className="card-title">{gapName}</span>
                                    {isObject && gap.effort && (
                                      <span className="effort-badge">{gap.effort}</span>
                                    )}
                                    {isObject && (
                                      <ChevronRight size={14} className={`expand-arrow ${isExpanded ? 'rotated' : ''}`} />
                                    )}
                                  </div>
                                  {isObject && isExpanded && (
                                    <div className="card-details">
                                      {gap.impact && (
                                        <div className="impact-row">
                                          <span className="impact-label">Impact if not addressed:</span>
                                          <p className="impact-text">{gap.impact}</p>
                                        </div>
                                      )}
                                      {gap.suggestedTask && (
                                        <button
                                          className="add-task-btn"
                                          onClick={(e) => { e.stopPropagation(); addGapAsTask(gap); }}
                                        >
                                          <Plus size={14} />
                                          <span>Add as Sub-Objective</span>
                                        </button>
                                      )}
                                    </div>
                                  )}
                                  {!isObject && (
                                    <button
                                      className="quick-add-btn"
                                      onClick={(e) => { e.stopPropagation(); addGapAsTask(gap); }}
                                      title="Add as task"
                                    >
                                      <Plus size={12} />
                                    </button>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* Key Insights */}
                      {aiAdvice.projectAnalysis.keyInsights?.length > 0 && (
                        <div className="analysis-section insights-section">
                          <div className="section-label">
                            <Sparkles size={14} />
                            <span>KEY INSIGHTS</span>
                          </div>
                          <div className="insights-grid">
                            {aiAdvice.projectAnalysis.keyInsights.map((insight, i) => (
                              <div
                                key={i}
                                className={`insight-card type-${insight.type}`}
                                onClick={() => toggleExpanded(setExpandedInsights, `insight-${i}`)}
                              >
                                <div className="insight-type-badge">{insight.type}</div>
                                <p className="insight-text">{insight.insight}</p>
                                {expandedInsights[`insight-${i}`] && insight.recommendation && (
                                  <div className="insight-recommendation">
                                    <span className="rec-label">Recommendation:</span>
                                    <p>{insight.recommendation}</p>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Project Info Section - Enhanced */}
                  {aiAdvice.projectInfo && (
                    <div className="interactive-project-info">
                      <div className="section-label">
                        <BookOpen size={14} />
                        <span>PROJECT INTELLIGENCE</span>
                      </div>

                      {/* Problem & Solution */}
                      <div className="info-card problem-solution">
                        <div className="info-block problem">
                          <span className="block-label">THE PROBLEM</span>
                          <p className="block-main">{aiAdvice.projectInfo.coreProblem}</p>
                          {aiAdvice.projectInfo.problemDetails && (
                            <p className="block-details">{aiAdvice.projectInfo.problemDetails}</p>
                          )}
                        </div>
                        <div className="info-divider">
                          <span>→</span>
                        </div>
                        <div className="info-block solution">
                          <span className="block-label">THE SOLUTION</span>
                          <p className="block-main">{aiAdvice.projectInfo.solution}</p>
                          {aiAdvice.projectInfo.solutionDifferentiator && (
                            <p className="block-differentiator">{aiAdvice.projectInfo.solutionDifferentiator}</p>
                          )}
                        </div>
                      </div>

                      {/* Target User */}
                      {aiAdvice.projectInfo.targetUser && (
                        <div className="info-card target-user">
                          <span className="block-label">TARGET USER</span>
                          {typeof aiAdvice.projectInfo.targetUser === 'object' ? (
                            <div className="user-persona">
                              <div className="persona-header">
                                <div className="persona-avatar">
                                  <User size={20} />
                                </div>
                                <div className="persona-info">
                                  <span className="persona-name">{aiAdvice.projectInfo.targetUser.persona}</span>
                                  <span className="persona-role">{aiAdvice.projectInfo.targetUser.role}</span>
                                </div>
                              </div>
                              {aiAdvice.projectInfo.targetUser.painPoints?.length > 0 && (
                                <div className="persona-section">
                                  <span className="mini-label">Pain Points:</span>
                                  <ul>
                                    {aiAdvice.projectInfo.targetUser.painPoints.map((p, i) => (
                                      <li key={i}>{p}</li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                              {aiAdvice.projectInfo.targetUser.goals?.length > 0 && (
                                <div className="persona-section goals">
                                  <span className="mini-label">Goals:</span>
                                  <ul>
                                    {aiAdvice.projectInfo.targetUser.goals.map((g, i) => (
                                      <li key={i}>{g}</li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                            </div>
                          ) : (
                            <p className="block-main">{aiAdvice.projectInfo.targetUser}</p>
                          )}
                        </div>
                      )}

                      {/* Key Features */}
                      {aiAdvice.projectInfo.keyFeatures?.length > 0 && (
                        <div className="info-card features">
                          <span className="block-label">KEY FEATURES</span>
                          <div className="features-list">
                            {aiAdvice.projectInfo.keyFeatures.map((f, i) => {
                              const isObject = typeof f === 'object';
                              const isExpanded = expandedFeatures[`feature-${i}`];
                              return (
                                <div
                                  key={i}
                                  className={`feature-item ${isObject ? `status-${f.status}` : ''} ${isExpanded ? 'expanded' : ''}`}
                                  onClick={() => isObject && toggleExpanded(setExpandedFeatures, `feature-${i}`)}
                                >
                                  <div className="feature-header">
                                    {isObject && <span className={`feature-status ${f.status}`}>{f.status}</span>}
                                    <span className="feature-name">{isObject ? f.name : f}</span>
                                  </div>
                                  {isObject && isExpanded && (
                                    <div className="feature-details">
                                      {f.description && <p>{f.description}</p>}
                                      {f.userBenefit && (
                                        <p className="user-benefit">
                                          <span>User benefit:</span> {f.userBenefit}
                                        </p>
                                      )}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* Tech Stack & Competitors Row */}
                      <div className="info-row">
                        {aiAdvice.projectInfo.techStack && (
                          <div className="info-card tech-stack">
                            <span className="block-label">TECH STACK</span>
                            {typeof aiAdvice.projectInfo.techStack === 'object' ? (
                              <div className="tech-grid">
                                {aiAdvice.projectInfo.techStack.frontend && (
                                  <div className="tech-item">
                                    <span className="tech-label">Frontend</span>
                                    <span className="tech-value">{aiAdvice.projectInfo.techStack.frontend}</span>
                                  </div>
                                )}
                                {aiAdvice.projectInfo.techStack.backend && (
                                  <div className="tech-item">
                                    <span className="tech-label">Backend</span>
                                    <span className="tech-value">{aiAdvice.projectInfo.techStack.backend}</span>
                                  </div>
                                )}
                                {aiAdvice.projectInfo.techStack.database && (
                                  <div className="tech-item">
                                    <span className="tech-label">Database</span>
                                    <span className="tech-value">{aiAdvice.projectInfo.techStack.database}</span>
                                  </div>
                                )}
                                {aiAdvice.projectInfo.techStack.infrastructure && (
                                  <div className="tech-item">
                                    <span className="tech-label">Infrastructure</span>
                                    <span className="tech-value">{aiAdvice.projectInfo.techStack.infrastructure}</span>
                                  </div>
                                )}
                              </div>
                            ) : (
                              <p className="tech-simple">{aiAdvice.projectInfo.techStack}</p>
                            )}
                          </div>
                        )}

                        {aiAdvice.projectInfo.competitors?.length > 0 && (
                          <div className="info-card competitors">
                            <span className="block-label">COMPETITIVE LANDSCAPE</span>
                            <div className="competitors-list">
                              {aiAdvice.projectInfo.competitors.map((c, i) => (
                                <div key={i} className="competitor-item">
                                  {typeof c === 'object' ? (
                                    <>
                                      <span className="competitor-name">{c.name}</span>
                                      <span className="competitor-weakness">{c.weakness}</span>
                                      {c.ourAdvantage && (
                                        <span className="our-advantage">Our edge: {c.ourAdvantage}</span>
                                      )}
                                    </>
                                  ) : (
                                    <span className="competitor-simple">{c}</span>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>

                      {aiAdvice.projectInfo.marketSize && (
                        <div className="info-card market">
                          <span className="block-label">MARKET SIZE</span>
                          <p className="market-size">{aiAdvice.projectInfo.marketSize}</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* ACTIONS TAB */}
              {activeTab === 'actions' && aiAdvice && (
                <div className="tab-panel actions-panel">
                  {/* Focusing Question */}
                  {aiAdvice.focusingQuestion && (
                    <div className="focusing-question-hero">
                      <Sparkles size={18} />
                      <p>"{aiAdvice.focusingQuestion}"</p>
                    </div>
                  )}

                  {/* Immediate Actions */}
                  {aiAdvice.immediateActions?.length > 0 && (
                    <div className="immediate-actions-section">
                      <div className="section-header">
                        <Rocket size={16} />
                        <h3>IMMEDIATE ACTIONS</h3>
                      </div>
                      <div className="actions-list">
                        {aiAdvice.immediateActions.map((action, i) => (
                          <div key={i} className={`action-card priority-${i + 1}`}>
                            <div className="action-priority">#{i + 1}</div>
                            <div className="action-content">
                              <h4>{action.action}</h4>
                              <p className="action-details">{action.details}</p>
                              <div className="action-meta">
                                <span className="time-estimate"><Clock size={12} /> {action.timeEstimate}</span>
                                <span className="action-why">{action.why}</span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* DELIVERABLES TAB */}
              {activeTab === 'deliverables' && aiAdvice && (
                <div className="tab-panel deliverables-panel">
                  {aiAdvice.requiredDeliverables && (
                    <div className="deliverables-section">
                      <div className="section-header">
                        <ListChecks size={16} />
                        <h3>REQUIRED DELIVERABLES</h3>
                      </div>
                      <div className="deliverables-grid">
                        {Object.entries(aiAdvice.requiredDeliverables).map(([key, value]) => (
                          <div key={key} className={`deliverable-card status-${value.status}`}>
                            <div className="deliverable-header">
                              <span className="deliverable-name">{key.replace(/([A-Z])/g, ' $1').toUpperCase()}</span>
                              <span className={`status-badge ${value.status}`}>{value.status?.toUpperCase()}</span>
                            </div>
                            {value.needed?.length > 0 && (
                              <ul className="needed-list">
                                {value.needed.map((item, i) => (
                                  <li key={i}>{item}</li>
                                ))}
                              </ul>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* GUIDANCE TAB */}
              {activeTab === 'guidance' && aiAdvice && (
                <div className="tab-panel guidance-panel">
                  {aiAdvice.startupAdvice && (
                    <div className="startup-advice-section">
                      <div className="section-header">
                        <TrendingUp size={16} />
                        <h3>STARTUP GUIDANCE</h3>
                      </div>
                      <div className="startup-content">
                        <div className="startup-row">
                          <div className="startup-item">
                            <span className="startup-label">STAGE:</span>
                            <span className="startup-value">{aiAdvice.startupAdvice.stage}</span>
                          </div>
                          <div className="startup-item">
                            <span className="startup-label">NEXT MILESTONE:</span>
                            <span className="startup-value milestone">{aiAdvice.startupAdvice.milestone}</span>
                          </div>
                        </div>
                        <div className="startup-focus">
                          <div className="focus-item do">
                            <span className="focus-label"><Check size={14} /> FOCUS ON:</span>
                            <p>{aiAdvice.startupAdvice.focus}</p>
                          </div>
                          <div className="focus-item dont">
                            <span className="focus-label"><X size={14} /> AVOID:</span>
                            <p>{aiAdvice.startupAdvice.avoid}</p>
                          </div>
                        </div>
                        {aiAdvice.startupAdvice.metrics?.length > 0 && (
                          <div className="metrics-section">
                            <span className="metrics-label">KEY METRICS TO TRACK:</span>
                            <div className="metrics-list">
                              {aiAdvice.startupAdvice.metrics.map((m, i) => (
                                <span key={i} className="metric-badge">{m}</span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* DOCUMENTS TAB */}
              {activeTab === 'documents' && (
                <div className="tab-panel documents-panel">
                  <div className="generated-docs-section">
                    <div className="section-header">
                      <BookOpen size={16} />
                      <h3>PROJECT DOCUMENTS</h3>
                      {savingDocs && <span className="saving-indicator">Saving...</span>}
                    </div>
                    {(aiAdvice?.generatedDocuments?.length > 0 || projectDocs.length > 0) ? (
                      <div className="docs-grid">
                        {/* Show newly generated docs first */}
                        {aiAdvice?.generatedDocuments?.map((doc, i) => (
                          <div key={`new-${i}`} className={`doc-card doc-type-${doc.type} new-doc`}>
                            <div className="doc-header">
                              <span className="doc-type-badge">{doc.type?.replace('_', ' ').toUpperCase()}</span>
                              <span className="doc-new-badge">NEW</span>
                            </div>
                            <h4 className="doc-title">{doc.title}</h4>
                            <div className={`doc-content ${expandedDoc === `new-${i}` ? 'expanded' : ''}`}>
                              <pre>{doc.content}</pre>
                            </div>
                            <button
                              className="doc-expand-btn"
                              onClick={() => setExpandedDoc(expandedDoc === `new-${i}` ? null : `new-${i}`)}
                            >
                              {expandedDoc === `new-${i}` ? 'Collapse' : 'Expand'}
                            </button>
                          </div>
                        ))}
                        {/* Show existing saved docs */}
                        {projectDocs.map((doc, i) => (
                          <div key={`saved-${i}`} className={`doc-card doc-type-${doc.type}`}>
                            <div className="doc-header">
                              <span className="doc-type-badge">{doc.type?.replace('_', ' ').toUpperCase()}</span>
                              <button
                                className="doc-delete-btn"
                                onClick={() => deleteDoc(i)}
                                title="Delete document"
                              >
                                <X size={12} />
                              </button>
                            </div>
                            <h4 className="doc-title">{doc.title}</h4>
                            <div className={`doc-content ${expandedDoc === `saved-${i}` ? 'expanded' : ''}`}>
                              <pre>{doc.content}</pre>
                            </div>
                            <div className="doc-footer">
                              <span className="doc-created">
                                {doc.createdAt ? new Date(doc.createdAt).toLocaleDateString() : 'Unknown'}
                              </span>
                              <button
                                className="doc-expand-btn"
                                onClick={() => setExpandedDoc(expandedDoc === `saved-${i}` ? null : `saved-${i}`)}
                              >
                                {expandedDoc === `saved-${i}` ? 'Collapse' : 'Expand'}
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="empty-docs">
                        <BookOpen size={32} />
                        <p>No documents yet. Click "Regenerate" to generate project documentation.</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </main>
      </div>

      {/* Status Bar */}
      <footer className="objective-status-bar">
        <div className="status-group">
          <span className="status-label">PROJECT:</span>
          <span className="status-value">{project?.name}</span>
        </div>
        <div className="status-group">
          <span className="status-label">STATUS:</span>
          <span className={`status-value ${objectiveData?.completed ? 'status-complete' : 'status-active'}`}>
            {objectiveData?.completed ? 'COMPLETE' : 'IN PROGRESS'}
          </span>
        </div>
        <div className="status-group">
          <span className="status-label">PRIORITY:</span>
          <span className={`status-value priority-${priority}`}>{priority.toUpperCase()}</span>
        </div>
        <div className="status-group progress-group">
          <span className="status-label">OVERALL:</span>
          <div className="progress-bar">
            <div className="progress-fill" style={{ width: `${subItemProgress}%` }}></div>
          </div>
          <span className="status-value">{subItemProgress}%</span>
        </div>
      </footer>
    </div>
  );
};

export default ObjectiveDetailPage;
